/* global $ */
import Swal from 'sweetalert2';
import { get_token, getCookie } from '@/router/index.js';

export default {
  name: 'UserAccessControlTab',

  props: {
    user_id: {
      type: [String, Number],
      required: true,
    },
  },

  data: () => ({
    loading: false,
    saving_id: null,

    platforms: [],
    user_platform_accesses: [],
    platform_access_rows: [],
  }),

  computed: {
    platforms_total_count() {
      return this.platform_access_rows.length;
    },
    platforms_enabled_count() {
      return this.platform_access_rows.filter(p => p.status === 'ENABLED').length;
    },
    platforms_disabled_count() {
      return this.platform_access_rows.filter(p => p.status !== 'ENABLED').length;
    },
  },

  mounted() {
    this.fetch_platform_access_control();
    this.csrf_token = getCookie('csrftoken');
  },

  methods: {
    async fetch_platform_access_control() {
      this.loading = true;
      const token = get_token();

      try {
        await Promise.all([
          this.fetch_all_platforms(token),
          this.fetch_user_platform_accesses(token),
        ]);

        this.build_platform_access_rows();
        this.rebuild_table();
      } catch (err) {
        console.error(err);
        this.$notyf?.error('Failed to load platform access');
      } finally {
        this.loading = false;
      }
    },

    async fetch_all_platforms(token) {
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_platforms/`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': this.csrf_token,
          'X-Page-URL': window.location.href
        },
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || data?.status !== 'success') {
        throw new Error(data?.message || 'Failed to fetch platforms');
      }

      this.platforms = data.data || [];
    },

    async fetch_user_platform_accesses(token) {
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_user_platform_access/?user_id=${this.user_id}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': this.csrf_token
        },
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || data?.status !== 'success') {
        throw new Error(data?.message || 'Failed to fetch user platform access');
      }

      this.user_platform_accesses = data.data || [];
    },

    build_platform_access_rows() {
      const access_map = new Map();
      (this.user_platform_accesses || []).forEach((a) => {
        access_map.set(Number(a.platform_id), a);
      });

      this.platform_access_rows = (this.platforms || []).map((p) => {
        const existing = access_map.get(Number(p.id));
        return {
          platform_id: p.id,
          platform_name: p.name,
          status: existing?.status || 'DISABLED',
          date_created: existing?.date_created || null,
        };
      });
    },

    async confirm_set_platform_access(platform_id, action) {
      const label = action === 'ENABLE' ? 'enable' : 'revoke';

      const result = await Swal.fire({
        title: 'Confirm action',
        text: `Do you want to ${label} platform access for this user?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) return;

      await this.set_platform_access(platform_id, action);
    },

    async set_platform_access(platform_id, action) {
      const token = get_token();
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/set_user_platform_access/`;

      this.saving_id = platform_id;

      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrf_token,
          },
          body: JSON.stringify({
            user_id: this.user_id,
            platform_ids: [platform_id],
            action,          // "ENABLE" | "REVOKE"
            partial: true,
          }),
        });

        const data = await response.json().catch(() => null);

        if (!response.ok || data?.status !== 'success') {
          this.$notyf?.error(data?.message || 'Failed to update platform access');
          return;
        }

        this.$notyf?.success('Platform access updated');

        // refresh access only
        await this.fetch_user_platform_accesses(token);
        this.build_platform_access_rows();
        this.rebuild_table();

      } catch (err) {
        console.error(err);
        this.$notyf?.error('Error updating platform access');
      } finally {
        this.saving_id = null;
      }
    },

    rebuild_table() {
      this.$nextTick(() => {
        const table_el = $('#platform_access_table');
        if (!table_el.length || !$.fn.DataTable) return;

        if ($.fn.DataTable.isDataTable(table_el)) {
          table_el.DataTable().destroy();
        }

        table_el.DataTable({
          pageLength: 10,
          lengthChange: false,
          searching: true,
          ordering: true,
          paging: true,
          info: true,
          autoWidth: false,
        });
      });
    },
  }
};
